import { API_URL, getApiUrl } from '@/utils/api';
import ApiBase from '@/services/ApiBase';
import { CartAttributeKeys, CartModel, CartShippingModel, CartPayModeModel, OrderModel } from '@/models';
import { AjaxService } from '@/services/Ajax.service';
import { AppFullLocale, valueof } from '@/types';
import { CheckoutServiceInterface } from '@/services/CheckoutService.interface';
import { PaymentProviderInterface } from '@/services/PaymentProviders.strategy';
import { AppDispatch } from '@/redux';
import { AdyenComponentRef } from '@/services/Adyen.service';
import { RootState } from '@/redux/create-store';
import { PAYMENT_PLATFORMS, STEPS } from '@/constants/checkout';

export class CheckoutServiceV2 extends ApiBase implements CheckoutServiceInterface {
  static userCartUrl = getApiUrl(API_URL.USER, '/cart');
  static userOrderUrl = getApiUrl(API_URL.USER, '/order');
  static storeConfigUrl = getApiUrl(API_URL.ADMIN, '/config');
  static storageOrderUrl = getApiUrl(API_URL.USER, '/storageOrder');

  constructor(protected paymentProviders: PaymentProviderInterface, api: AjaxService) {
    super(api);
  }

  async updateDiscountCode(storeId: string, discountCode: string): Promise<CartModel> {
    return await this.api.putPromisedJSON(`${CheckoutServiceV2.userCartUrl}/${storeId}/updateDiscountCode`, { discountCode });
  }

  async getShippingModesForCart({ storeId }: { storeId: string }): Promise<Array<CartShippingModel>> {
    return await this.api.getPromisedJSON(`${CheckoutServiceV2.userCartUrl}/${storeId}/getShippings`);
  }

  async updateCartAttributeByName(
    { storeId, attributeName, newValue, currentStep = undefined } :
    {storeId: string, attributeName: CartAttributeKeys, newValue: unknown, currentStep?: valueof<typeof STEPS>}
  ): Promise<CartModel> {
    return await this.api.postPromisedJSON(`${CheckoutServiceV2.userCartUrl}/${storeId}/setCurrentCartAttribute`, { attributeName, newValue, origin: currentStep });
  }

  async getBlockingMotivesForShipping({ configKeyToRetrieve }: {configKeyToRetrieve: string}): Promise<Array<{
    value: string;
    label: string
  }>> {
    const key = new URLSearchParams({ key: configKeyToRetrieve });
    const [response] = await this.api.getPromisedJSON(`${CheckoutServiceV2.storeConfigUrl}/bykey?${key.toString()}`);

    if (!response || !response.value) {
      throw new Error('No response from the server');
    }

    return response?.value.split(';').map((subValue: string) => {
      const [value, label] = subValue.split(',');
      return { value, label };
    });
  }

  async getPaymentModesForStoreId({ storeId, isApplePayEnabled = false }: {
    storeId: string, isApplePayEnabled: boolean
  }): Promise<Array<CartPayModeModel>> {
    return await this.api.getPromisedJSON(`${CheckoutServiceV2.userCartUrl}/${storeId}/getPayments?isApplePayEnabled=${isApplePayEnabled}`);
  }

  async loadPaymentProvider(
    paymentProvider: valueof<typeof PAYMENT_PLATFORMS>,
    options?: {
      storeId: string, paymentMode: CartPayModeModel, onlySaveCard?: boolean,
      binding?: AdyenComponentRef, cartId?: string, getState?: () => RootState,
      translate?: (key: string) => string
    }
  ) {
    return await this.paymentProviders.load(paymentProvider, options);
  }

  async initPaymentProvider(
    options: {locale: AppFullLocale, username: string, password: string},
    configuration?: Record<string, unknown>
  ) {
    return this.paymentProviders.initPaymentProvider(options, configuration);
  }

  async createPaymentProviderForm(
    paymentMode: CartPayModeModel, chosenPaymentProvider: valueof<typeof PAYMENT_PLATFORMS>, dispatch: AppDispatch
  ) {
    try {
      return await this.paymentProviders.createPaymentProviderForm(paymentMode, chosenPaymentProvider, dispatch);
    } catch (error) {
      return error;
    }
  }

  async processPayment({ cart }): Promise<any> {
    return this.paymentProviders.processPayment(cart);
  }

  async validatePayment(cart: CartModel, optionalData: {details: unknown} | null = null): Promise<unknown> {
    return await this.paymentProviders.validatePayment(cart, optionalData);
  }

  async getUserLastOrder({ orderId }: {orderId?: string} = {}): Promise<{ order: OrderModel }> {
    if (!orderId) {
      const order = await this.api.getPromisedJSON(`${CheckoutServiceV2.userOrderUrl}/getLastOrder`);
      return { order };
    }
    const order = await this.api.getPromisedJSON(`${CheckoutServiceV2.storageOrderUrl}/getStorageOrderByOrderId/${orderId}`);
    return { order };
  }

  async addProductToCartFromCheckout(
    { storeId, partnumber, quantity } : {storeId: string, partnumber: string, quantity: string},
  ) {
    return await this.api.postPromisedJSON(`${CheckoutServiceV2.userCartUrl}/${storeId}/product`, {
      partnumber,
      qty: parseInt(quantity),
    }, {
      guestToken: true,
      credentials: 'include'
    });
  }
}
